import { useState } from "react";
import apiClient from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, CheckCircle, Warning, X } from "@phosphor-icons/react";

/**
 * StripeLookupCard
 * Allows admin to search Stripe by email and find/activate users whose
 * payments did NOT register in the local DB (e.g. paid before customer_id was saved).
 */
export default function StripeLookupCard() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activating, setActivating] = useState(false);

  const search = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Ingresa un email válido");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await apiClient.get(`/admin/stripe/lookup-by-email?email=${encodeURIComponent(email)}`);
      setResult(r.data);
      if (r.data?.error) {
        toast.error(r.data.error);
      } else if (!r.data?.customers?.length) {
        toast.warning("No hay clientes en Stripe con ese email");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error consultando Stripe");
    }
    setLoading(false);
  };

  const activate = async () => {
    setActivating(true);
    try {
      const r = await apiClient.post("/admin/stripe/activate-from-email", { email });
      toast.success(`✅ ${r.data.message} — vence ${r.data.expires_at?.slice(0, 10) || "?"}`);
      // Re-search to refresh
      await search();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error activando");
    }
    setActivating(false);
  };

  const canActivate = result?.user_in_db && result?.customers?.some(c => c.can_be_activated);

  return (
    <div
      data-testid="stripe-lookup-card"
      className="rounded-xl p-5 border"
      style={{ background: "rgba(168,85,247,0.04)", borderColor: "rgba(168,85,247,0.25)" }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold mb-1" style={{ color: "#A855F7" }}>
            🔎 Stripe Lookup
          </div>
          <div className="text-sm font-semibold text-white">Buscar pago en Stripe por email</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Si un usuario pagó pero no aparece en la lista de "Activaciones Pendientes", busca aquí su email para encontrar su pago en Stripe.
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          type="email"
          placeholder="cliente@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          className="flex-1 bg-black/30 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
          data-testid="stripe-lookup-email-input"
        />
        <button
          onClick={search}
          disabled={loading || !email}
          className="px-4 py-2.5 rounded-lg text-sm font-bold disabled:opacity-50 transition-all"
          style={{
            background: "rgba(168,85,247,0.15)",
            color: "#A855F7",
            border: "1px solid rgba(168,85,247,0.4)",
          }}
          data-testid="stripe-lookup-search-btn"
        >
          {loading ? "Buscando…" : <><MagnifyingGlass weight="bold" className="w-4 h-4 inline mr-1" />Buscar</>}
        </button>
      </div>

      {result && (
        <div className="space-y-3 mt-4">
          {/* Usuario en DB */}
          <div className="rounded-lg p-3" style={{
            background: result.user_in_db ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)",
            border: result.user_in_db ? "1px solid rgba(16,185,129,0.3)" : "1px solid rgba(239,68,68,0.3)",
          }}>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-1"
                 style={{ color: result.user_in_db ? "#10B981" : "#F87171" }}>
              {result.user_in_db ? <CheckCircle weight="fill" className="w-4 h-4" /> : <X weight="bold" className="w-4 h-4" />}
              Usuario en DB
            </div>
            {result.user_in_db ? (
              <div className="text-xs text-zinc-300">
                <strong>{result.user_in_db.name || "Sin nombre"}</strong> — plan: {result.user_in_db.plan} · status: <strong>{result.user_in_db.subscription_status}</strong>
                {result.user_in_db.subscription_expires_at && <> · vence {result.user_in_db.subscription_expires_at.slice(0, 10)}</>}
                <div className="text-[10px] text-zinc-500 mt-1">
                  customer_id: {result.user_in_db.stripe_customer_id || "—"} · sub_id: {result.user_in_db.stripe_subscription_id || "—"}
                </div>
              </div>
            ) : (
              <div className="text-xs text-zinc-300">
                ⚠️ Este email NO existe en tu base de datos. Si pagó, primero crea su cuenta usuario.
              </div>
            )}
          </div>

          {/* Customers en Stripe */}
          {result.customers?.length === 0 ? (
            <div className="rounded-lg p-3 text-xs text-zinc-400" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)" }}>
              ❌ No se encontró ningún cliente en Stripe con este email.
            </div>
          ) : result.customers?.map((c, idx) => (
            <div key={c.id} className="rounded-lg p-3" style={{
              background: c.can_be_activated ? "rgba(16,185,129,0.06)" : "rgba(255,255,255,0.03)",
              border: c.can_be_activated ? "1px solid rgba(16,185,129,0.3)" : "1px solid rgba(255,255,255,0.06)",
            }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-bold text-white truncate">
                  Customer #{idx + 1}: {c.name || c.email}
                </div>
                <span className="text-[10px] text-zinc-600">{c.created?.slice(0, 10)}</span>
              </div>
              <div className="text-[10px] text-zinc-500 mb-2 truncate">ID: {c.id}</div>

              {c.subscriptions?.length > 0 && (
                <div className="space-y-1 mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Suscripciones</div>
                  {c.subscriptions.map(s => (
                    <div key={s.id} className="text-[11px] text-zinc-300 flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                        s.status === "trialing" || s.status === "active"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-zinc-700/40 text-zinc-400"
                      }`}>
                        {s.status}
                      </span>
                      <span className="font-mono">{s.id?.slice(0, 24)}…</span>
                      {s.current_period_end && <span className="text-zinc-500">· hasta {s.current_period_end.slice(0, 10)}</span>}
                    </div>
                  ))}
                </div>
              )}

              {c.recent_invoices?.length > 0 && (
                <div className="space-y-1 mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Facturas recientes</div>
                  {c.recent_invoices.map(inv => (
                    <div key={inv.id} className="text-[11px] text-zinc-300 flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                        inv.status === "paid" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700/40 text-zinc-400"
                      }`}>
                        {inv.status}
                      </span>
                      <span>${Number(inv.amount_paid ?? 0).toFixed(2)}</span>
                      <span className="text-zinc-500">· {inv.created?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-start gap-2 text-[11px] text-zinc-400 mt-2 pt-2 border-t border-white/5">
                <Warning className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{c.recommendation}</span>
              </div>
            </div>
          ))}

          {/* Activate button */}
          {canActivate && (
            <button
              onClick={activate}
              disabled={activating}
              className="w-full py-3 rounded-lg text-xs font-black uppercase tracking-widest disabled:opacity-50 transition-all"
              style={{
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                color: "white",
              }}
              data-testid="stripe-lookup-activate-btn"
            >
              {activating ? "Activando…" : "✅ Activar este usuario ahora"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
